import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import PageHeader from "../components/PageHeader";
import LoadingState from "../components/LoadingState";
import EmptyState from "../components/EmptyState";
import { toast } from "sonner";

const TABS = [
    { key: "anagrafica", label: "Anagrafica" },
    { key: "comunicazioni", label: "Comunicazioni" },
    { key: "ibans", label: "IBAN autorizzati" },
    { key: "cronologia", label: "Cronologia" },
    { key: "note", label: "Note" },
];

export default function SupplierDetailPage() {
    const { id } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState("anagrafica");

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/suppliers/${id}`);
            setData(data);
        } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

    if (loading || !data) return <LoadingState />;
    const s = data.supplier;

    return (
        <div className="space-y-4" data-testid="supplier-detail">
            <PageHeader
                title={s.ragione_sociale}
                subtitle={`P.IVA ${s.partita_iva}`}
                breadcrumbs={[{ label: "Fornitori", to: "/suppliers" }, { label: s.ragione_sociale }]}
            />

            <div className="flex gap-1 text-xs border-b border-[#DCE3EE]">
                {TABS.map(t => (
                    <button key={t.key} data-testid={`supplier-tab-${t.key}`} onClick={() => setTab(t.key)}
                        className={`px-4 py-2 font-medium ${tab === t.key ? "text-[#0B1633] border-b-2 border-[#2563EB]" : "text-[#64748B]"}`}>
                        {t.label}
                    </button>
                ))}
            </div>

            {tab === "anagrafica" && (
                <div className="bg-white border border-[#DCE3EE] rounded-xl p-5 space-y-2">
                    <Row k="Ragione sociale" v={s.ragione_sociale} />
                    <Row k="P.IVA" v={s.partita_iva} />
                    <Row k="Codice fiscale" v={s.codice_fiscale} />
                    <Row k="Categoria" v={s.category} />
                    <Row k="Indirizzi PEC" v={(s.pec_addresses || []).join(", ")} />
                    <Row k="Email" v={(s.email_addresses || []).join(", ")} />
                    <Row k="Domini autorizzati" v={(s.authorized_domains || []).join(", ")} />
                    <Row k="Note" v={s.notes} />
                </div>
            )}
            {tab === "comunicazioni" && (
                <div className="bg-white border border-[#DCE3EE] rounded-xl overflow-hidden">
                    {(data.recent_pec || []).length === 0 ? (
                        <div className="p-4 text-sm text-[#64748B]">Nessuna PEC recente da questo fornitore</div>
                    ) : (
                        <table className="w-full text-sm">
                            <thead className="bg-[#F5F7FB] text-[11px] uppercase text-[#64748B]">
                                <tr><th className="text-left px-4 py-3">Data</th>
                                    <th className="text-left px-4 py-3">Oggetto</th>
                                    <th className="text-left px-4 py-3">Stato tecnico</th>
                                    <th className="text-left px-4 py-3">Azioni</th></tr>
                            </thead>
                            <tbody className="divide-y divide-[#DCE3EE]">
                                {data.recent_pec.map(p => (
                                    <tr key={p.id}>
                                        <td className="px-4 py-2 text-xs">{(p.created_at || "").slice(0, 10)}</td>
                                        <td className="px-4 py-2 text-xs">{p.subject}</td>
                                        <td className="px-4 py-2 text-xs">{p.technical_status}</td>
                                        <td className="px-4 py-2 text-xs"><Link to={`/pec/analyses/${p.id}`} className="text-[#2563EB] hover:underline">Apri</Link></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}
            {tab === "ibans" && (
                <div className="bg-white border border-[#DCE3EE] rounded-xl overflow-hidden">
                    {(data.ibans || []).length === 0 ? (
                        <div className="p-4 text-sm text-[#64748B]">Nessun IBAN autorizzato</div>
                    ) : (
                        <table className="w-full text-sm">
                            <thead className="bg-[#F5F7FB] text-[11px] uppercase text-[#64748B]">
                                <tr><th className="text-left px-4 py-3">IBAN</th>
                                    <th className="text-left px-4 py-3">Intestatario</th>
                                    <th className="text-left px-4 py-3">Banca</th>
                                    <th className="text-left px-4 py-3">Stato</th></tr>
                            </thead>
                            <tbody className="divide-y divide-[#DCE3EE]">
                                {data.ibans.map(i => (
                                    <tr key={i.id}>
                                        <td className="px-4 py-2 text-xs font-mono">{i.iban}</td>
                                        <td className="px-4 py-2 text-xs">{i.account_holder}</td>
                                        <td className="px-4 py-2 text-xs">{i.bank_name}</td>
                                        <td className="px-4 py-2 text-xs">{i.status}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}
            {tab === "cronologia" && <EmptyState title="Cronologia verifiche" description="Nessun evento registrato per questo fornitore." />}
            {tab === "note" && <EmptyState title="Note" description="Le note per fornitore saranno disponibili nelle prossime fasi." />}
        </div>
    );
}

function Row({ k, v }) {
    return (
        <div className="flex text-xs border-b border-[#F5F7FB] last:border-0 py-1.5">
            <div className="text-[#64748B] w-40 shrink-0">{k}</div>
            <div className="text-[#172033]">{v || "—"}</div>
        </div>
    );
}
